import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Camera, Zap, MapPin, Briefcase, Users, DollarSign, ArrowUpRight, TrendingUp, CloudSun } from "lucide-react";
import { TimeClockPunchCard } from "@/components/timeclock/TimeClockPunchCard";
import { MobileStormPulse } from "@/components/mobile/MobileStormPulse";
import { MobileTodaysHustle } from "@/components/mobile/MobileTodaysHustle";
import { PremiumIcon } from "@/components/mobile/PremiumIcon";
import { cn } from "@/lib/utils";

function GlassCard({
  className,
  children,
  onClick,
}: {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border/50",
        "bg-gradient-to-br from-card/95 via-card/80 to-card/60 backdrop-blur-xl",
        "shadow-[0_4px_20px_-4px_hsl(var(--background)/0.5)]",
        "dark:border-white/[0.06] dark:bg-gradient-to-br dark:from-white/[0.04] dark:via-white/[0.02] dark:to-transparent",
        "dark:shadow-[0_8px_32px_-8px_rgb(0_0_0_/_0.6),inset_0_1px_0_0_rgb(255_255_255_/_0.06)]",
        onClick && "cursor-pointer active:scale-[0.98] transition-transform",
        className
      )}
    >
      {/* subtle top sheen for premium glass feel */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent dark:via-white/15" />
      {children}
    </div>
  );
}

function StatTile({
  icon,
  value,
  label,
  tone = "primary",
}: {
  icon: typeof Briefcase;
  value: string | number;
  label: string;
  tone?: "primary" | "amber" | "emerald" | "blue" | "violet" | "cyan" | "rose" | "orange";
}) {
  return (
    <GlassCard className="p-3.5">
      <div className="flex items-start justify-between mb-2">
        <PremiumIcon icon={icon} tone={tone} size="sm" />
      </div>
      <div className="text-2xl font-bold tracking-tight leading-none">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1.5 font-medium">{label}</div>
    </GlassCard>
  );
}

function PipelineRow({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className={cn("h-2 w-2 rounded-full shadow-[0_0_8px_currentColor]", color)} />
      <div className="flex-1 text-sm font-medium">{label}</div>
      <div className="text-sm font-bold tabular-nums">{count}</div>
    </div>
  );
}

export function MobileDashboard() {
  const navigate = useNavigate();
  const { user, orgId } = useAuth();

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = user?.email?.split("@")[0]?.split(/[._]/)[0] || "there";

  const { data: stats } = useQuery({
    queryKey: ["mobile-dashboard-stats", user?.id, orgId],
    enabled: Boolean(user?.id && orgId),
    queryFn: async () => {
      const [jobsData, leadsData] = await Promise.all([
        supabase.from("jobs").select("estimated_value, status", { count: "exact" }).eq("org_id", orgId),
        supabase.from("leads").select("status", { count: "exact" }).eq("org_id", orgId),
      ]);
      const revenue = jobsData.data?.reduce((s, j) => s + (j.estimated_value || 0), 0) || 0;
      const byStatus = (rows: any[] | null, key: string) =>
        (rows || []).filter((r) => (r.status || "").toLowerCase().includes(key)).length;

      return {
        jobs: jobsData.count || 0,
        leads: leadsData.count || 0,
        revenue,
        newLeads: byStatus(leadsData.data, "new"),
        quoted: byStatus(jobsData.data, "quote"),
        inProgress: byStatus(jobsData.data, "progress") || byStatus(jobsData.data, "active"),
        completed: byStatus(jobsData.data, "complet"),
      };
    },
  });

  return (
    <div className="w-full max-w-2xl mx-auto px-4 pt-3 pb-6 space-y-4">
      {/* Greeting + weather pill */}
      <div className="flex items-end justify-between pt-1">
        <div>
          <div className="text-xs font-medium text-muted-foreground">{greeting},</div>
          <h1 className="text-2xl font-bold tracking-tight capitalize">{firstName}</h1>
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-muted/60 backdrop-blur px-3 py-1.5">
          <CloudSun className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium">Good day to knock</span>
        </div>
      </div>

      {/* Hero quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/measurements")}
          className="relative overflow-hidden h-28 rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/70 text-primary-foreground p-4 text-left shadow-[0_10px_30px_-8px_hsl(var(--primary)/0.7),inset_0_1px_0_0_rgb(255_255_255_/_0.25)] active:scale-[0.98] transition-transform ring-1 ring-inset ring-white/15"
        >
          <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
          <div className="absolute -left-4 -bottom-6 h-20 w-20 rounded-full bg-black/20 blur-2xl" />
          <Zap className="relative h-6 w-6 mb-2 drop-shadow-[0_0_8px_rgb(255_255_255_/_0.6)]" strokeWidth={2.5} />
          <div className="relative text-base font-bold leading-tight">Instant Quote</div>
          <div className="relative text-[11px] opacity-90 mt-0.5">Full claims flow →</div>
        </button>
        <button
          onClick={() => navigate("/field-capture")}
          className="relative overflow-hidden h-28 rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-card/70 dark:border-white/[0.06] dark:from-white/[0.05] dark:via-white/[0.02] dark:to-transparent p-4 text-left active:scale-[0.98] transition-transform shadow-[0_4px_16px_-4px_hsl(var(--background)/0.5)]"
        >
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-cyan-400/10 blur-2xl dark:bg-cyan-400/20" />
          <PremiumIcon icon={Camera} tone="cyan" size="md" className="mb-2" />
          <div className="text-base font-bold leading-tight">Field Scout</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Photos · video · damage</div>
        </button>
      </div>

      {/* Time clock — keep existing card, wrapped for spacing */}
      <TimeClockPunchCard source="mobile" />

      {/* KPI grid */}
      <div className="grid grid-cols-3 gap-3">
        <StatTile icon={Briefcase} value={stats?.jobs ?? 0} label="Active Jobs" tone="blue" />
        <StatTile icon={Users} value={stats?.leads ?? 0} label="Hot Leads" tone="amber" />
        <StatTile
          icon={DollarSign}
          value={`$${((stats?.revenue || 0) / 1000).toFixed(0)}k`}
          label="Pipeline"
          tone="emerald"
        />
      </div>

      {/* Pipeline overview list */}
      <GlassCard className="p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold tracking-tight">Pipeline</h3>
          <button
            onClick={() => navigate("/pipeline")}
            className="flex items-center gap-1 text-[11px] font-medium text-primary"
          >
            View all <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
        <div className="divide-y divide-border/40">
          <PipelineRow label="New Leads" count={stats?.newLeads ?? 0} color="bg-blue-500 text-blue-500" />
          <PipelineRow label="Quoted" count={stats?.quoted ?? 0} color="bg-amber-500 text-amber-500" />
          <PipelineRow label="In Progress" count={stats?.inProgress ?? 0} color="bg-orange-500 text-orange-500" />
          <PipelineRow label="Completed" count={stats?.completed ?? 0} color="bg-emerald-500 text-emerald-500" />
        </div>
      </GlassCard>

      {/* Shortcut row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/canvassing")}
          className="rounded-2xl border border-border/60 bg-card dark:border-white/[0.06] dark:bg-white/[0.03] p-4 flex items-center gap-3 active:scale-[0.98] transition-transform shadow-[0_2px_10px_-2px_hsl(var(--background)/0.4)]"
        >
          <PremiumIcon icon={MapPin} tone="violet" />
          <div className="text-left">
            <div className="text-sm font-semibold leading-tight">Canvass</div>
            <div className="text-[11px] text-muted-foreground">Door-by-door</div>
          </div>
        </button>
        <button
          onClick={() => navigate("/jobs")}
          className="rounded-2xl border border-border/60 bg-card dark:border-white/[0.06] dark:bg-white/[0.03] p-4 flex items-center gap-3 active:scale-[0.98] transition-transform shadow-[0_2px_10px_-2px_hsl(var(--background)/0.4)]"
        >
          <PremiumIcon icon={TrendingUp} tone="cyan" />
          <div className="text-left">
            <div className="text-sm font-semibold leading-tight">All Jobs</div>
            <div className="text-[11px] text-muted-foreground">Active pipeline</div>
          </div>
        </button>
      </div>

      {/* Storm Pulse — interactive 5-day weather + canvassing intel */}
      <MobileStormPulse />

      {/* Today's Hustle — daily goal rings, streak, leaderboard */}
      <MobileTodaysHustle />
    </div>
  );
}
